import React from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Menu,
    MenuItem,
    ListItemText,
    Alert,
    AlertTitle,
    CircularProgress
} from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import i18n from '../../i18n';
import { TemplateService } from '../../services/templatesServise/TemplateService';

export default function TemplateButton({
    label,
    loadTemplates,
    fieldMapping = TemplateService.defaultFieldMapping,
    onApply,
    disabled
}) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [templates, setTemplates] = React.useState([]);
    const open = Boolean(anchorEl);

    const handleOpen = async (e) => {
        setAnchorEl(e.currentTarget);
        if (templates.length === 0 && typeof loadTemplates === 'function') {
            setLoading(true);
            setError(null);
            try {
                const list = await TemplateService.loadTemplates(
                    loadTemplates,
                    fieldMapping
                );
                setTemplates(list);
            } catch (err) {
                setError(
                    (err && (err.message || String(err))) ||
                        'Failed to load templates'
                );
            } finally {
                setLoading(false);
            }
        }
    };

    const handleClose = () => setAnchorEl(null);

    const handleApply = (tpl) => {
        handleClose();
        if (typeof onApply === 'function') {
            onApply(tpl);
        }
    };

    return (
        <>
            <Button
                startIcon={
                    loading ? (
                        <CircularProgress size={18} />
                    ) : (
                        <ContentPasteIcon />
                    )
                }
                onClick={handleOpen}
                disabled={disabled || loading}
            >
                {label}
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        style: { minWidth: 250 }
                    }
                }}
            >
                {loading && (
                    <MenuItem disabled>
                        <ListItemText primary={i18n.t('common.loading')} />
                    </MenuItem>
                )}

                {!loading && error && (
                    <MenuItem disabled>
                        <Alert severity="warning" sx={{ m: 0, p: 0 }}>
                            <AlertTitle>{i18n.t('common.error')}</AlertTitle>
                            {error}
                        </Alert>
                    </MenuItem>
                )}

                {!loading && !error && templates.length === 0 && (
                    <MenuItem disabled>
                        <ListItemText
                            primary={i18n.t('text.avaliableTemplatesNotFound')}
                        />
                    </MenuItem>
                )}

                {!loading &&
                    !error &&
                    templates.map((tpl) => (
                        <MenuItem key={tpl.id} onClick={() => handleApply(tpl)}>
                            <ListItemText primary={tpl.name} />
                        </MenuItem>
                    ))}
            </Menu>
        </>
    );
}

TemplateButton.propTypes = {
    label: PropTypes.string.isRequired,
    loadTemplates: PropTypes.func, // () => Promise<{data: any[]}>
    fieldMapping: PropTypes.object, // mapping server -> dialogData
    onApply: PropTypes.func.isRequired, // (tpl) => void
    disabled: PropTypes.bool
};
